import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Navbar } from "./navbar";
import { ArrowLeft, Lock, Package } from "lucide-react";
import { addCommande, deleteCartItem, getDataCommandes } from "./action";
import { generateInvoicePDF } from "../utils/generateInvoice";
import "../styles/paiement.css";

export function Paiement() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // We expect { method, total, orderInfo } to be passed via route state
    const { method, total, orderInfo, items } = location.state || {};
    const { commandes } = useSelector(state => state);

    const [cardInfo, setCardInfo] = useState({
        number: "",
        name: "",
        expiry: "",
        cvc: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Redirect if no method selected (e.g., direct navigation)
    useEffect(() => {
        if (!method) {
            navigate("/valider");
        }
        dispatch(getDataCommandes());
    }, [method, navigate, dispatch]);

    if (!method) return null;

    const handleCardChange = (e) => {
        setCardInfo({ ...cardInfo, [e.target.name]: e.target.value });
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        // 1. Prepare New Order
        const nextId = (commandes.length + 1).toString();
        const newOrder = {
            id: nextId,
            username_Client: orderInfo?.nom || "Client",
            total: total,
            date: new Date().toLocaleDateString('fr-FR'),
            statut: "en attente",
            method: method,
            adresse: orderInfo?.adresse,
            ville: orderInfo?.ville,
            telephone: orderInfo?.telephone,
            produits: items.map(item => ({
                id: item.originalId || item.id,
                nom: item.nomClimatisseur,
                quantite: item.quantite,
                prix: item.prix,
                taille: item.taille,
                color: item.color
            }))
        };

        // 2. Save Order to data.json
        try {
            await dispatch(addCommande(newOrder)).unwrap();

            // 3. Clear Cart (delete each item on server)
            try {
                const deletePromises = items
                    .filter(item => item.id) // Ensure ID exists to avoid 404 on /api/cart/undefined
                    .map(item => dispatch(deleteCartItem(item.id)).unwrap());
                await Promise.all(deletePromises);
            } catch (cartErr) {
                console.warn("Could not clear some cart items:", cartErr);
                // Non-blocking error: the order is already saved
            }

            // 4. Clear checkout persistence from localStorage
            localStorage.removeItem('checkoutInfo');
            localStorage.removeItem('paiementMethod');

            // Generate and Download PDF Invoice
            await generateInvoicePDF(newOrder, items);

            // Create WhatsApp message with order summary
            const adminPhone = "212624784965";
            const productsList = items.map(item => 
                `▪ ${item.quantite || 1}x ${item.nomClimatisseur || item.nom} (${item.taille || "Standard"}${item.color ? " / " + item.color : ""})`
            ).join("\n");
            
            const message = `Bonjour, voici ma commande N°${newOrder.id}.\n\n*Résumé de la commande:*\n${productsList}\n\n*Total:* ${total?.toLocaleString()} DH\n\nVeuillez m'envoyer votre RIB pour payer l'avance de 30% (ou le montant total).`;
            
            const whatsappUrl = `https://wa.me/${adminPhone}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, '_blank');

            navigate("/");
            
        } catch (err) {
            console.error("Erreur lors de la validation de la commande:", err);
            alert(`Une erreur est survenue: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="payment-gateway-page">
            <Navbar />

            <div className="payment-gateway-container">
                <div className="payment-card-wrapper">

                    <div className="payment-header">
                        <button className="back-btn-payment" onClick={() => navigate(-1)}>
                            <span>←</span> Retour
                        </button>
                        <h2>Paiement Sécurisé</h2>
                        <div className="payment-badges">
                            <span><Lock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '5px' }} /> 100% Sécurisé</span>
                        </div>
                    </div>

                    <div className="payment-amount-box">
                        <span>Montant à régler</span>
                        <h2>{total?.toLocaleString()} DH</h2>
                        {orderInfo?.nom && <p>Commande pour: {orderInfo.nom}</p>}
                    </div>



                    {method === "livraison" && (
                        <div className="cod-payment-container">
                            <div className="cod-icon-big"><Package size={80} color="#000033" /></div>
                            <h3>Paiement à la livraison</h3>
                            <p className="cod-desc">
                                Votre commande sera enregistrée et la facture téléchargée.
                                Vous serez redirigé vers <strong>WhatsApp</strong> pour envoyer la facture et payer l'avance (30%) ou la totalité via virement bancaire pour l'adresse suivante :
                            </p>
                            <div className="address-review-box">
                                {orderInfo?.adresse}<br />
                                {orderInfo?.ville}<br />
                                {orderInfo?.telephone}
                            </div>

                            <button 
                                onClick={handlePaymentSubmit} 
                                className="btn-cod-confirm"
                                disabled={isSubmitting}
                                style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                            >
                                {isSubmitting ? "Traitement en cours..." : "Confirmer ma commande"}
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
